import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import {
  FiHome,
  FiMail,
  FiBriefcase,
  FiUsers,
  FiList,
  FiSettings,
  FiUser,
  FiGlobe,
  FiKey,
  FiChevronDown,
  FiChevronUp,
  FiFileText,
} from "react-icons/fi";
import "./Sidebar.css";
import logo from "../../../assets/Praxiti-Logo.png";

const Sidebar = ({ activeMenuItem, setActiveMenuItem }) => {
  const [isUserManagementOpen, setIsUserManagementOpen] = useState(false);
  const [isWebsiteManagementOpen, setIsWebsiteManagementOpen] = useState(false);
  const { user } = useAuth();

  // Submenus
  const userManagementSubItems = [
    "super-admin-users",
    "admin-users",
    "users",
    "roles",
    "privileges",
    "tenants",
    "applications",
    "tenant-applications",
    "user-applications",
    "tenant-application-default-roles",
  ];

  const websiteManagementSubItems = [
    "sections",
    "subsections",
    "events",
    "contact-us",
    "job-postings",
    "job-applications",
  ];

  const isAnyUserManagementSubItemActive =
    userManagementSubItems.includes(activeMenuItem);
  const isAnyWebsiteManagementSubItemActive =
    websiteManagementSubItems.includes(activeMenuItem);

  // Expand User Management if active
  useEffect(() => {
    if (isAnyUserManagementSubItemActive && !isUserManagementOpen) {
      setIsUserManagementOpen(true);
    }
  }, [activeMenuItem, isAnyUserManagementSubItemActive, isUserManagementOpen]);

  // Expand Website Management if active
  useEffect(() => {
    if (isAnyWebsiteManagementSubItemActive) {
      setIsWebsiteManagementOpen(true);
    }
  }, [activeMenuItem]);

  // Handlers
  const handleUserManagementToggle = () =>
    setIsUserManagementOpen(!isUserManagementOpen);
  const handleWebsiteManagementToggle = () =>
    setIsWebsiteManagementOpen(!isWebsiteManagementOpen);

  const handleSubMenuItemClick = (item) => setActiveMenuItem(item);

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        {user?.tenantLogo ? (
          <img
            src={user?.tenantLogo}
            alt="tenant-logo"
            style={{
              width: "120px",
              objectFit: "contain",
              mixBlendMode: "lighten",
            }}
          />
        ) : (
        <img
          src={logo}
          alt="Praxiti Logo"
          className="sidebar-logo"
        />
        )}
        <h3>{user?.tenantName || "Admin Panel"}</h3>
      </div>

      <ul className="sidebar-menu">
        {/* Dashboard */}
        <li
          className={activeMenuItem === "dashboard" ? "active" : ""}
          onClick={() => setActiveMenuItem("dashboard")}
        >
          <FiHome size={20} /> <span>Dashboard</span>
        </li>

        {/* Website Management Dropdown */}
        <li
          className={`menu-item-parent ${
            isAnyWebsiteManagementSubItemActive ? "active-parent" : ""
          } ${isWebsiteManagementOpen ? "open" : ""}`}
        >
          <div
            className="menu-item-title"
            onClick={handleWebsiteManagementToggle}
          >
            <FiGlobe size={20} /> <span>Website Management</span>
            {isWebsiteManagementOpen ? (
              <FiChevronUp size={20} className="dropdown-icon" />
            ) : (
              <FiChevronDown size={20} className="dropdown-icon" />
            )}
          </div>
          <ul className="submenu">
            <li
              className={activeMenuItem === "sections" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("sections")}
            >
              <FiList size={18} /> <span>Sections</span>
            </li>
            <li
              className={activeMenuItem === "subsections" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("subsections")}
            >
              <FiList size={18} /> <span>Subsections</span>
            </li>
            <li
              className={activeMenuItem === "events" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("events")}
            >
              <FiList size={18} /> <span>Events</span>
            </li>
            <li
              className={activeMenuItem === "contact-us" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("contact-us")}
            >
              <FiMail size={18} /> <span>Contact Us</span>
            </li>
            <li
              className={activeMenuItem === "job-postings" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("job-postings")}
            >
              <FiBriefcase size={18} /> <span>Job Postings</span>
            </li>
            <li
              className={activeMenuItem === "job-applications" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("job-applications")}
            >
              <FiUsers size={18} /> <span>Job Applications</span>
            </li>
          </ul>
        </li>

        {/* User Management Dropdown */}
        <li
          className={`menu-item-parent ${
            isAnyUserManagementSubItemActive ? "active-parent" : ""
          } ${isUserManagementOpen ? "open" : ""}`}
        >
          <div className="menu-item-title" onClick={handleUserManagementToggle}>
            <FiSettings size={20} /> <span>User Management</span>
            {isUserManagementOpen ? (
              <FiChevronUp size={20} className="dropdown-icon" />
            ) : (
              <FiChevronDown size={20} className="dropdown-icon" />
            )}
          </div>
          <ul className="submenu">
            <li
              className={activeMenuItem === "super-admin-users" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("super-admin-users")}
            >
              <FiUser size={18} /> <span>Super Admin Users</span>
            </li>
            <li
              className={activeMenuItem === "admin-users" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("admin-users")}
            >
              <FiUser size={18} /> <span>Admin Users</span>
            </li>
            <li
              className={activeMenuItem === "users" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("users")}
            >
              <FiUsers size={18} /> <span>Users</span>
            </li>
            <li
              className={activeMenuItem === "roles" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("roles")}
            >
              <FiKey size={18} /> <span>Application Roles</span>
            </li>
            <li
              className={activeMenuItem === "privileges" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("privileges")}
            >
              <FiList size={18} /> <span>Application Privileges</span>
            </li>
            <li
              className={activeMenuItem === "tenants" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("tenants")}
            >
              <FiGlobe size={18} /> <span>Tenants</span>
            </li>

            <li
              className={activeMenuItem === "applications" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("applications")}
            >
              <FiFileText size={18} /> <span>Applications</span>
            </li>
            <li
              className={
                activeMenuItem === "tenant-applications" ? "active" : ""
              }
              onClick={() => handleSubMenuItemClick("tenant-applications")}
            >
              <FiFileText size={18} /> <span>Tenant Applications</span>
            </li>
            <li
              className={activeMenuItem === "user-applications" ? "active" : ""}
              onClick={() => handleSubMenuItemClick("user-applications")}
            >
              <FiFileText size={18} /> <span>User Applications Role</span>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  );
};

export default Sidebar;
